import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CopyButton } from "@/components/common/CopyButton";
import { SyncJob, SyncJobProgress } from "@/types/pms";
import { formatDateTimeUtc, formatDuration } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { syncJobStatusClass } from "../utils/syncJobVariants";

interface SyncJobDetailDialogProps {
  job: SyncJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="text-sm">{children}</span>
  </div>
);

const successRate = (p: SyncJobProgress): string => {
  if (p.processed <= 0) return "—";
  const rate = ((p.created + p.updated) / p.processed) * 100;
  return `${rate.toFixed(rate === 100 || rate === 0 ? 0 : 1)}%`;
};

export const SyncJobDetailDialog = ({
  job,
  open,
  onOpenChange,
}: SyncJobDetailDialogProps) => {
  const { t } = useTranslation();

  if (!job) return null;

  const progress = Array.isArray(job.progress) ? job.progress : [];
  const entitiesWithErrors = progress.filter((p) => p.errors.length > 0);
  const duration = formatDuration(job.startedAt, job.completedAt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("pms.sync.jobDetail.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-5 overflow-auto">
          <div className="grid grid-cols-1 gap-4 rounded-md border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetaRow label={t("pms.sync.jobDetail.jobId")}>
              <div className="flex items-center gap-1.5">
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {job.id}
                </code>
                <CopyButton
                  value={job.id}
                  label={t("pms.sync.jobDetail.copy")}
                  successMessage={t("pms.sync.jobDetail.copied")}
                />
              </div>
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.type")}>
              <Badge variant="outline">{job.type}</Badge>
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.status")}>
              <Badge className={cn("border-0", syncJobStatusClass[job.status])}>
                {job.status}
              </Badge>
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.createdAt")}>
              {formatDateTimeUtc(job.createdAt)}
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.startedAt")}>
              {job.startedAt ? formatDateTimeUtc(job.startedAt) : "—"}
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.completedAt")}>
              {job.completedAt ? formatDateTimeUtc(job.completedAt) : "—"}
            </MetaRow>
            <MetaRow label={t("pms.sync.jobDetail.duration")}>
              {duration || "—"}
            </MetaRow>
          </div>

          {progress.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t("pms.sync.jobDetail.noProgress")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pms.sync.jobDetail.entityType")}</TableHead>
                  <TableHead className="text-right">
                    {t("pms.sync.jobDetail.processed")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pms.sync.jobDetail.created")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pms.sync.jobDetail.updated")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pms.sync.jobDetail.failed")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pms.sync.jobDetail.successRate")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {progress.map((p) => (
                  <TableRow key={p.entityType}>
                    <TableCell>
                      <Badge variant="outline">{p.entityType}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{p.processed}</TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        p.created > 0 && "font-medium",
                      )}
                    >
                      {p.created}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        p.updated > 0 && "font-medium",
                      )}
                    >
                      {p.updated}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        p.failed > 0 && "font-medium text-destructive",
                      )}
                    >
                      {p.failed}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {successRate(p)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {job.error && (
            <div className="flex flex-col gap-2">
              <h4 className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {t("pms.sync.jobDetail.failureReason")}
              </h4>
              <pre className="max-h-40 overflow-auto rounded border bg-muted/50 p-3 text-xs whitespace-pre-wrap break-words">
                {job.error}
              </pre>
            </div>
          )}

          {entitiesWithErrors.length > 0 && (
            <div className="flex flex-col gap-3">
              <h4 className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {t("pms.sync.jobDetail.entityErrors")}
              </h4>
              {entitiesWithErrors.map((p) => (
                <ErrorBlock key={p.entityType} progress={p} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ErrorBlock = ({ progress }: { progress: SyncJobProgress }) => (
  <div className="rounded border bg-muted/50 p-3">
    <Badge variant="outline" className="mb-2">
      {progress.entityType}
    </Badge>
    <div className="max-h-32 overflow-auto text-xs text-muted-foreground">
      {progress.errors.map((err, idx) => (
        <p key={`${progress.entityType}-${idx}`} className="py-0.5">
          {err}
        </p>
      ))}
    </div>
  </div>
);
